import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PublicNav from '../components/PublicNav';

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < breakpoint);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
}

const BLUE = '#2563eb';
const WHITE = '#ffffff';
const TEXT = '#0a0a0a';
const TEXT_MUTED = '#555555';
const BORDER = '#e2e8f0';
const OFF_WHITE = '#f8fafc';
const FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 36 }}>
      <h2 style={{ fontSize: 19, fontWeight: 800, color: TEXT, marginBottom: 12 }}>{title}</h2>
      <div style={{ fontSize: 15, color: TEXT_MUTED, lineHeight: 1.75 }}>{children}</div>
    </div>
  );
}

export default function Terms() {
  const isMobile = useIsMobile();
  const px = isMobile ? 20 : 60;

  return (
    <div style={{ background: WHITE, minHeight: '100vh', fontFamily: FONT }}>
      <PublicNav isMobile={isMobile} />

      <div style={{ padding: isMobile ? `40px ${px}px 60px` : `64px ${px}px 80px`, maxWidth: 720, margin: '0 auto' }}>
        <div style={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.1em', color: BLUE, marginBottom: 14 }}>Legal</div>
        <h1 style={{ fontSize: isMobile ? 30 : 40, fontWeight: 800, color: TEXT, letterSpacing: '-1px', lineHeight: 1.1, marginBottom: 10 }}>
          Terms of Service
        </h1>
        <div style={{ fontSize: 13, color: TEXT_MUTED, marginBottom: 40 }}>Last updated: July 9, 2026</div>

        <Section title="Agreement to terms">
          <p style={{ margin: 0 }}>
            By creating an account or using previsr.com ("PreVisr", "we", "us"), you agree to these Terms of Service. If you don't agree, please don't use the site.
          </p>
        </Section>

        <Section title="What PreVisr is">
          <p style={{ margin: 0 }}>
            PreVisr is a tool that helps pre-medical students organize their school list and track their application. Our matching results are estimates built from historical admissions data and our own scoring methodology &mdash; they are informational only and are <strong style={{ color: TEXT }}>not a guarantee or prediction of admission</strong> to any school. Admissions decisions are made solely by the schools themselves, based on your complete application.
          </p>
        </Section>

        <Section title="Your account">
          <p style={{ margin: 0 }}>
            You're responsible for keeping your login credentials secure and for all activity under your account. You must provide accurate information when creating an account, and you must be at least 13 years old to use PreVisr.
          </p>
        </Section>

        <Section title="Subscriptions and billing">
          <p style={{ marginBottom: 12 }}>
            Some features require a paid subscription. Payments are processed by Stripe; by subscribing, you authorize recurring charges at the price shown at checkout until you cancel.
          </p>
          <p style={{ margin: 0 }}>
            You can cancel anytime from your account settings; cancellation takes effect at the end of the current billing period. Subscription fees are non-refundable except where required by law.
          </p>
        </Section>

        <Section title="Acceptable use">
          <p style={{ marginBottom: 12 }}>You agree not to:</p>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            <li style={{ marginBottom: 6 }}>Access or attempt to access other users' accounts or data without authorization.</li>
            <li style={{ marginBottom: 6 }}>Scrape, reverse-engineer, or resell the school-matching data or methodology.</li>
            <li style={{ marginBottom: 6 }}>Use the service for any unlawful purpose or to submit false application information to schools.</li>
            <li>Interfere with or disrupt the service, including through automated abuse of our systems.</li>
          </ul>
        </Section>

        <Section title="Intellectual property">
          <p style={{ margin: 0 }}>
            The PreVisr name, site, and underlying matching methodology are our property. Your academic data and any content you submit (like saved schools or personal statement drafts) remain yours &mdash; we don't claim ownership over it.
          </p>
        </Section>

        <Section title="Disclaimer of warranties">
          <p style={{ margin: 0 }}>
            PreVisr is provided "as is." We work to keep school data accurate and up to date, but we don't warrant that every figure is current or error-free, and we are not affiliated with or endorsed by any medical school, AAMC, or AACOM. Use your judgment, and verify deadlines and requirements directly with each school.
          </p>
        </Section>

        <Section title="Limitation of liability">
          <p style={{ margin: 0 }}>
            To the fullest extent permitted by law, PreVisr is not liable for any indirect, incidental, or consequential damages arising from your use of the service, including decisions you make about where to apply. Our total liability for any claim is limited to the amount you paid us in the 12 months before the claim arose.
          </p>
        </Section>

        <Section title="Termination">
          <p style={{ margin: 0 }}>
            You can stop using PreVisr and delete your account at any time. We may suspend or terminate accounts that violate these terms.
          </p>
        </Section>

        <Section title="Changes to these terms">
          <p style={{ margin: 0 }}>
            We may update these terms from time to time. If we make material changes, we'll update the date above and, where appropriate, notify you by email.
          </p>
        </Section>

        <Section title="Contact us">
          <p style={{ margin: 0 }}>
            Questions about these terms? Email us at <a href="mailto:support@previsr.com" style={{ color: BLUE }}>support@previsr.com</a>.
          </p>
        </Section>

        <div style={{ marginTop: 48, padding: '16px 20px', background: OFF_WHITE, border: '1px solid ' + BORDER, borderRadius: 10, fontSize: 13, color: TEXT_MUTED, lineHeight: 1.6 }}>
          Also see our <Link to="/privacy" style={{ color: BLUE, fontWeight: 600 }}>Privacy Policy</Link>.
        </div>
      </div>
    </div>
  );
}
